/**
 * Entity configuration for ExtraFieldDynamicForm
 * This file centralizes the entity-specific configuration to make the form easily extensible
 */

export type EntityType = 'lead' | 'serviceRequest' | 'branch' | 'branchService' | 'serviceType';

export interface EntityConfig {
  idKey: string; // The prop name for the entity ID (e.g., 'leadId', 'serviceRequestId')
  dispatchType: string; // The Redux action type to dispatch
  payloadIdKey: string; // The key name in the payload (e.g., 'leadId', 'serviceRequestId')
}

/**
 * Configuration map for each entity type
 * Add new entity types here to extend the form functionality
 */
export const ENTITY_CONFIG_MAP: Record<EntityType, EntityConfig> = {
  lead: {
    idKey: 'leadId',
    dispatchType: 'leads/updateLeadExtraField',
    payloadIdKey: 'leadId',
  },
  serviceRequest: {
    idKey: 'serviceRequestId',
    dispatchType: 'serviceRequest/updateServiceRequestExtraField',
    payloadIdKey: 'serviceRequestId',
  },
  branch: {
    idKey: 'branchId',
    dispatchType: '',
    payloadIdKey: 'branchId',
  },
  branchService: {
    idKey: 'branchServiceId',
    dispatchType: '',
    payloadIdKey: 'branchServiceId',
  },
  serviceType: {
    idKey: 'serviceTypeId',
    dispatchType: '',
    payloadIdKey: 'serviceTypeId',
  },
};

/**
 * Helper to get entity configuration from props
 * Returns the first matching entity configuration based on provided IDs
 * Optionally takes relatedTo to prioritize the correct entity
 */
export const getEntityConfig = (
  props: Record<string, any>,
  relatedTo?: string
): { entityConfig: EntityConfig; entityId: string } | null => {
  // If relatedTo is provided, try to find the specific config first
  if (relatedTo) {
    let entityType: EntityType | null = null;

    // Map related_to values to EntityType keys
    if (relatedTo === 'lead' || relatedTo === 'customer') {
      entityType = 'lead';
    } else if (relatedTo === 'serviceRequest' || relatedTo === 'result') {
      entityType = 'serviceRequest';
    } else if (ENTITY_CONFIG_MAP[relatedTo as EntityType]) {
      entityType = relatedTo as EntityType;
    }

    if (entityType) {
      const config = ENTITY_CONFIG_MAP[entityType];
      const entityId = props[config.idKey];
      if (entityId) {
        return { entityConfig: config, entityId };
      }
    }
  }

  // Fallback to existing logic: find the first available ID in order of ENTITY_CONFIG_MAP
  for (const entityType of Object.keys(ENTITY_CONFIG_MAP) as EntityType[]) {
    const config = ENTITY_CONFIG_MAP[entityType];
    const entityId = props[config.idKey];
    if (entityId) {
      return { entityConfig: config, entityId };
    }
  }
  return null;
};
